import { Flex, LoaderCover, Progress, Text, toaster } from 'components'
import Button from 'components/Button'
import DeleteConfirmButton from 'components/DeleteConfirmButton'
import Panel from 'components/Panel'
import { useAlert, useCurrentUser, useWorkspace } from 'components/providers'
import { isEqual, startCase } from 'lodash-es'
import { useEffect, useRef, useState } from 'react'
import {
  helperLeadProperties,
  ILeadLookupConfig,
  LookupOutputProp,
  primaryLeadProperties,
} from 'types'
import {
  cleanupLookupValueDocs,
  createTimestamp,
  deleteDoc,
  getDocById,
  getDocs,
  useBeforeUnload,
  useDoc,
} from 'utils'
import { SmartColumnConfigEditor, SmartColumnConfigViewer } from '.'
import {
  cleanDuplicates,
  isSmartColumnInput,
  parseLookupValuesForUpdate,
  parseNewLookupValues,
  updateLookUpConfig,
  updateLookupValues,
  useWorkspaceSmartColumns,
} from '../../utils/lookupUtils'

interface Props {
  lookUpId: string
}

export default function SmartColumnConfigurer({ lookUpId }: Props) {
  const [leadLookUpConfig] = useDoc('leadLookupConfig', lookUpId)
  const user = useCurrentUser()
  const { updateWorkspace, workspace } = useWorkspace()
  const [fileLoading, setFileLoading] = useState(false)
  const [dbFields, setDbFields] = useState<string[]>()
  const [rawDbData, setRawDbData] = useState<Record<string, string>[]>()
  const [localConfig, setLocalConfig] = useState<Partial<ILeadLookupConfig>>()
  const [dbInputProps, setDbInputProps] = useState<string[]>()
  const [lookupOutputProps, setLookupOutputProps] = useState<LookupOutputProp[]>([
    { index: 0, dbField: '', dbFieldName: '', hasChart: false },
  ])
  const [progress, setProgress] = useState(0)
  const uploadController = useRef(new AbortController())
  const [error, setError] = useState<string>('')
  const [saveEnabled, setSaveEnabled] = useState(false)
  const [editing, setEditing] = useState(false)
  const isUsedAsInput = isSmartColumnInput(
    leadLookUpConfig?.outputProperties?.map(prop => prop.name) || [],
    workspace?.id!
  )
  const { confirm } = useAlert()
  const momentifyFields = [
    ...primaryLeadProperties,
    ...helperLeadProperties,
    ...useWorkspaceSmartColumns(workspace?.id!),
  ].sort((a, b) => a.localeCompare(b))

  const cleanUp = () => {
    cleanupLookupValueDocs({ id: lookUpId })
  }
  useBeforeUnload(progress > 0, uploadController.current.abort, cleanUp)

  useEffect(() => {
    if (!leadLookUpConfig) return
    setLocalConfig(leadLookUpConfig)
  }, [leadLookUpConfig])

  useEffect(() => {
    const localConfigIsUpdated =
      localConfig?.inputProperties && !isEqual(leadLookUpConfig, localConfig)
    if (localConfigIsUpdated) setSaveEnabled(true)
    else setSaveEnabled(false)
  }, [localConfig])

  useEffect(() => {
    if (dbInputProps) {
      if (rawDbData && dbInputProps.length === localConfig?.inputProperties?.length) {
        const { cleanedData, duplicatesNum } = cleanDuplicates(rawDbData, dbInputProps)
        if (duplicatesNum > 0) alertDuplicates(duplicatesNum, cleanedData)
      }
    }
    setLocalConfig({
      ...localConfig,
      dbInputProperties: dbInputProps,
    })
  }, [dbInputProps])

  useEffect(() => {
    const outputProps = lookupOutputProps
      .map(row => ({
        name: row.dbFieldName !== '' ? row.dbFieldName : row.dbField,
        dbField: row.dbField,
        hasChart: row.hasChart,
      }))
      .filter(Boolean)
    setLocalConfig({
      ...localConfig,
      outputProperties: outputProps.map(prop => ({
        name: prop.name,
        dbField: prop.dbField,
        hasChart: prop.hasChart,
      })),
    })
  }, [lookupOutputProps])

  useEffect(() => {
    if (error) {
      setSaveEnabled(false)
      if (!error.includes('Update for values was cancelled but some values were updated'))
        updateLookUpConfig(user?.id!, lookUpId!, { status: 'invalid' })
      toaster.create({ title: error, type: 'error' })
      setError('')
      setSaveEnabled(true)
    }
  }, [error])

  useEffect(() => {
    if (progress === 100) {
      handleSaveConfig()
    } else if (progress > 0) setSaveEnabled(false)
  }, [progress])

  function resetData() {
    setDbInputProps(undefined)
    setDbFields(undefined)
    setRawDbData(undefined)
    if (leadLookUpConfig?.outputProperties)
      setLookupOutputProps(
        leadLookUpConfig?.outputProperties?.map((row, index) => ({
          index: index,
          dbField: row.dbField,
          dbFieldName: row.name,
          hasChart: row.hasChart,
        }))
      )
    else setLookupOutputProps([{ index: 0, dbField: '', dbFieldName: '', hasChart: false }])
    if (leadLookUpConfig && localConfig)
      setLocalConfig({ ...leadLookUpConfig, inputProperties: localConfig.inputProperties })
    setProgress(0)
  }

  async function resetConfig() {
    resetData()
    setEditing(false)
    const leadConfig = await getDocById('leadLookupConfig', lookUpId)
    if (leadConfig) setLocalConfig(leadConfig)
  }

  function handleSaveConfig() {
    updateLookUpConfig(user!.id, lookUpId, { ...localConfig, status: 'success' })
    setEditing(false)
    setSaveEnabled(true)
    resetData()
  }

  async function handleSave() {
    if (!user || !localConfig) return
    if (editing) {
      if (rawDbData) {
        setFileLoading(true)
        const leadLookupValues = await getDocs({
          collection: 'leadLookupValues',
          constraints: [{ field: 'configId', op: '==', value: lookUpId }],
        })
        setFileLoading(false)
        const { batchRows, newRows, updatedRows, deletedRows } = parseLookupValuesForUpdate(
          rawDbData,
          localConfig.inputProperties!,
          dbInputProps!,
          localConfig.outputProperties!.map(prop => prop.name),
          lookupOutputProps.map(row => row.dbField),
          lookUpId,
          workspace?.id!,
          leadLookupValues.data
        )
        if (batchRows.length === 0) {
          toaster.create({
            title: 'No changes detected. Save complete',
            type: 'success',
          })
          setProgress(100)
          return
        } else
          toaster.create({
            title: `Adding ${newRows} new rows, updating ${updatedRows} rows, deleting ${deletedRows} rows`,
            type: 'info',
          })
        updateLookupValues(
          user.id,
          batchRows,
          setProgress,
          setError,
          uploadController.current.signal
        )
      } else {
        handleSaveConfig()
      }
    } else {
      if (!rawDbData) return
      const lookupValueDocs = parseNewLookupValues(
        rawDbData,
        localConfig.inputProperties!,
        dbInputProps!,
        localConfig.outputProperties!.map(prop => prop.name),
        lookupOutputProps.map(row => row.dbField),
        lookUpId,
        workspace?.id!
      )

      updateLookupValues(
        user.id,
        lookupValueDocs,
        setProgress,
        setError,
        uploadController.current.signal,
        cleanUp
      )
    }
  }

  async function handleDelete() {
    if (!workspace) return

    await deleteDoc('leadLookupConfig', lookUpId!)
    const lookUpIds = (workspace.smartColumnsConfig?.lookUpIds || []).filter(id => id !== lookUpId)
    await updateWorkspace({
      smartColumnsConfig: {
        ...workspace.smartColumnsConfig,
        updated: createTimestamp(),
        lookUpIds,
      },
    })
  }

  async function alertDuplicates(duplicatesNum: number, cleanedData?: Record<string, string>[]) {
    const confirmed = await confirm(
      `This input field contains ${duplicatesNum} duplicate values. If you wish to continue with using the first value found for each duplicate, click "Confirm", or click "Cancel" and reupload a cleaned data file.`
    )
    if (confirmed) setRawDbData(cleanedData)
    else resetData()
  }

  if (!user || !workspace || !lookUpId) return null

  return (
    <LoaderCover display="flex" show={fileLoading}>
      <Panel flex={1} p={4} gap={6} width="auto">
        {lookUpId && (
          <Flex direction="column" gap={6}>
            <Flex align="center" justify="space-between" gap={1}>
              {editing ? (
                <Flex direction="column" gap={6} width="100%">
                  <SmartColumnConfigEditor
                    leadLookUpConfig={localConfig!}
                    momentifyFields={momentifyFields}
                    updateLocalLookUpConfig={setLocalConfig}
                    onDataReset={resetData}
                    rawDbData={rawDbData!}
                    setRawDbData={setRawDbData}
                    dbFields={dbFields}
                    setDbFields={setDbFields}
                    setDbInputProps={setDbInputProps}
                    setLookupOutputProps={setLookupOutputProps}
                    disableInputs={Boolean(progress)}
                    setSaveEnabled={setSaveEnabled}
                    pageIsLoading={setFileLoading}
                  />
                  <Flex gap={3} h="40px">
                    <Button
                      variant="solid"
                      palette="primary"
                      onClick={handleSave}
                      disabled={!saveEnabled}
                    >
                      Save
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        resetConfig()
                        if (progress > 0) uploadController.current.abort()
                      }}
                    >
                      Cancel
                    </Button>
                    <DeleteConfirmButton
                      h="100%"
                      disabled={Boolean(progress) || isUsedAsInput}
                      onDelete={handleDelete}
                      description={
                        localConfig?.status !== 'initialized'
                          ? `This will delete the configuration for the '${startCase(
                              localConfig?.title ||
                                localConfig?.inputProperties?.join(', ') ||
                                'smart column'
                            )}' as well as update all leads in live events with this change. Type "Delete" to confirm you wish to continue`
                          : 'Type "Delete" to confirm'
                      }
                    />
                  </Flex>
                </Flex>
              ) : (
                <SmartColumnConfigViewer
                  leadLookUpConfig={leadLookUpConfig!}
                  disableEdit={Boolean(progress) || isUsedAsInput}
                  onEdit={() => setEditing(true)}
                />
              )}
            </Flex>
          </Flex>
        )}
        {progress > 0 && (
          <Flex gap={2} align="center">
            <Progress
              h="5px"
              w="100%"
              background="gray.500"
              borderRadius="999px"
              colorPalette="secondary"
              css={{ '& > div': { transition: 'width 100ms ease' } }}
              value={progress}
            />
            <Text textStyle="bodyXSmall">{Math.floor(progress)}%</Text>
          </Flex>
        )}
      </Panel>
    </LoaderCover>
  )
}
